import React, { useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Helmet } from "react-helmet-async";
import "./ServicesPage.css";

gsap.registerPlugin(ScrollTrigger);

/* ══════════════════════════════════════════════════════════
   DATA
══════════════════════════════════════════════════════════ */
const services = [
  {
    id: "01",
    title: "Career Counselling",
    headline: "Find the right path before you find the right country.",
    desc: "Our counsellors assess your academic background, strengths, interests, and career goals to build a personalised roadmap. We help you shortlist the right course, level (UG/PG), and specialisation — before even looking at universities.",
    points: [
      "Academic profile evaluation",
      "Career aptitude assessment",
      "Course & specialisation guidance",
      "Gap year & re-application strategy",
    ],
    icon: (
      <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" />
        <polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76" />
      </svg>
    ),
  },
  {
    id: "02",
    title: "University Selection",
    headline: "100+ universities. Filtered down to your perfect fit.",
    desc: "We match your profile against thousands of programs globally to create a balanced shortlist — aspirational, target, and safe options. We consider ranking, scholarship potential, location, employability, and your budget.",
    points: [
      "Profile-based university shortlisting",
      "Ranking & ROI analysis",
      "Scholarship & funding research",
      "Application deadline management",
    ],
    icon: (
      <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 21h18" /><path d="M3 7v1a3 3 0 0 0 6 0V7m0 1a3 3 0 0 0 6 0V7m0 1a3 3 0 0 0 6 0V7H3l2-4h14l2 4" />
        <line x1="5" y1="21" x2="5" y2="10" /><line x1="19" y1="21" x2="19" y2="10" />
        <path d="M9 21v-4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v4" />
      </svg>
    ),
  },
  {
    id: "03",
    title: "Application Assistance",
    headline: "Every document, perfected.",
    desc: "We guide you through the entire application process — SOPs, LORs, essays, transcripts, and online submissions. Our team reviews and refines each document to ensure it tells your story compellingly and meets each university's specific requirements.",
    points: [
      "Statement of Purpose (SOP) drafting",
      "Letter of Recommendation guidance",
      "Resume & CV building",
      "Online application form support",
    ],
    icon: (
      <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
        <line x1="16" y1="13" x2="8" y2="13" />
        <line x1="16" y1="17" x2="8" y2="17" />
        <polyline points="10 9 9 9 8 9" />
      </svg>
    ),
  },
  {
    id: "04",
    title: "Visa Assistance",
    headline: "100% visa success in 2025. Here's how.",
    desc: "Visa applications are where many students stumble. Our team handles every detail — document checklists, financial proofs, cover letters, and mock interviews. We've maintained a 100% visa success rate through meticulous preparation.",
    points: [
      "Student visa documentation",
      "Financial proof structuring",
      "Cover letter for visa officers",
      "Mock visa interview preparation",
    ],
    icon: (
      <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
        <line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" />
        <line x1="3" y1="10" x2="21" y2="10" />
        <path d="M8 14h.01" /><path d="M12 14h.01" /><path d="M16 14h.01" />
        <path d="M8 18h.01" /><path d="M12 18h.01" /><path d="M16 18h.01" />
      </svg>
    ),
  },
  {
    id: "05",
    title: "Test Preparation",
    headline: "The score that opens the door.",
    desc: "We guide you on IELTS, TOEFL, GRE, GMAT, and SAT strategy — from what score you actually need, to partnered coaching centres with proven results. Our advisors track your progress and help you decide the best test timeline.",
    points: [
      "Exam requirement mapping by university",
      "IELTS / TOEFL strategy sessions",
      "GRE / GMAT coaching referrals",
      "Score improvement timeline planning",
    ],
    icon: (
      <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
      </svg>
    ),
  },
  {
    id: "06",
    title: "Pre-Departure & Settlement",
    headline: "From offer letter to landing — we don't disappear.",
    desc: "Once you have your visa, the journey is just beginning. We run orientation sessions covering accommodation, banking, travel, insurance, and cultural adaptation. You land knowing exactly what to do next.",
    points: [
      "Accommodation & housing guidance",
      "Travel & forex assistance",
      "Health insurance & banking advice",
      "Pre-departure orientation workshop",
    ],
    icon: (
      <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.11 12 19.79 19.79 0 0 1 1.04 3.4 2 2 0 0 1 3 1.22h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
      </svg>
    ),
  },
];

const destinations = [
  { name: "USA", count: "450+", img: "https://images.unsplash.com/photo-1496442226666-8d4d0e62e6e9?q=80&w=800&auto=format&fit=crop", slug: '/study-in/usa' },
  { name: "United Kingdom", count: "280+", img: "https://images.unsplash.com/photo-1513635269975-59663e0ac1ad?q=80&w=800&auto=format&fit=crop", slug: '/study-in/uk' },
  { name: "Australia", count: "190+", img: "https://images.unsplash.com/photo-1506973035872-a4ec16b8e8d9?q=80&w=800&auto=format&fit=crop", slug: '/study-in/australia' },
  { name: "Canada", count: "120+", img: "https://images.unsplash.com/photo-1517935706615-2717063c2225?q=80&w=800&auto=format&fit=crop", slug: '/study-in/canada' },
  { name: "Germany", count: "80+", img: "https://miro.medium.com/v2/0*lm35oJDt_4ZS11tT.jpeg", slug: '/study-in/germany' },
];

const processSteps = [
  { num: "1", label: "Free Evaluation", desc: "Book a free consultation. We assess your profile, budget, and goals." },
  { num: "2", label: "Personalised Plan", desc: "Receive a curated university shortlist and step-by-step action plan." },
  { num: "3", label: "Application & Docs", desc: "We help you craft your application and prepare all documentation." },
  { num: "4", label: "Visa & Departure", desc: "We prepare your visa application and orient you before you fly." },
];

/* ══════════════════════════════════════════════════════════
   COMPONENT
══════════════════════════════════════════════════════════ */
export default function ServicesPage() {
  const rootRef = useRef(null);
  const servicesRef = useRef(null);
  const servicesProgressRef = useRef(null);
  const processRef = useRef(null);
  const procLineRef = useRef(null);
  const destRef = useRef(null);
  const ctaRef = useRef(null);
  const ctaBgRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const ctx = gsap.context(() => {

      /* ─── HERO ─── line-mask title reveal + staggered pills */
      const heroTl = gsap.timeline({ delay: 0.15, defaults: { ease: "power4.out" } });
      heroTl
        .to(".sp-hero-eyebrow", { opacity: 1, y: 0, duration: 0.8 })
        .to(".sp-hero-title .line-inner", { y: 0, duration: 1.2, stagger: 0.11 }, "-=0.4")
        .to(".sp-hero-sub", { opacity: 1, y: 0, duration: 0.85, ease: "power3.out" }, "-=0.6")
        .fromTo(".sp-hero-pills span",
          { opacity: 0, y: 16, scale: 0.9 },
          { opacity: 1, y: 0, scale: 1, duration: 0.55, stagger: 0.07, ease: "back.out(1.4)" },
          "-=0.5"
        );

      /* ─── SERVICES ─── top progress rail fills as you scroll through */
      if (servicesRef.current && servicesProgressRef.current) {
        gsap.to(servicesProgressRef.current, {
          scaleX: 1,
          ease: "none",
          scrollTrigger: {
            trigger: servicesRef.current,
            start: "top 30%",
            end: "bottom 70%",
            scrub: 1,
          },
        });
      }

      /* ─── Per-service row animations ─── */
      gsap.utils.toArray(".sp-service-row").forEach((row) => {
        const bgNum = row.querySelector(".sp-service-bg-num");
        const icon = row.querySelector(".sp-service-icon");
        const title = row.querySelectorAll(".sp-service-title .line-inner");
        const headline = row.querySelector(".sp-service-headline");
        const desc = row.querySelector(".sp-service-desc");
        const points = row.querySelectorAll(".sp-service-points li");
        const cta = row.querySelector(".sp-service-cta");

        // Big faded number enters from below + scales in
        if (bgNum) {
          gsap.fromTo(bgNum,
            { opacity: 0, y: 80, scale: 1.1 },
            {
              opacity: 1, y: 0, scale: 1, duration: 1.2, ease: "power3.out",
              scrollTrigger: { trigger: row, start: "top 75%", once: true }
            }
          );
          // Parallax on scroll-through
          gsap.to(bgNum, {
            yPercent: -25, ease: "none",
            scrollTrigger: {
              trigger: row,
              start: "top bottom",
              end: "bottom top",
              scrub: true,
            },
          });
        }

        // Icon scale-in
        if (icon) {
          gsap.fromTo(icon,
            { opacity: 0, scale: 0.7, rotation: -6 },
            {
              opacity: 1, scale: 1, rotation: 0, duration: 0.9, ease: "back.out(1.4)",
              scrollTrigger: { trigger: row, start: "top 72%", once: true }
            }
          );
        }

        // Title line-mask reveal
        if (title.length) {
          gsap.to(title, {
            y: 0, duration: 1.1, stagger: 0.08, ease: "power4.out",
            scrollTrigger: { trigger: row, start: "top 70%", once: true },
          });
        }

        // Sequential fade-ups
        gsap.fromTo([headline, desc].filter(Boolean),
          { opacity: 0, y: 24 },
          {
            opacity: 1, y: 0, duration: 0.85, stagger: 0.12, ease: "power3.out",
            scrollTrigger: { trigger: row, start: "top 70%", once: true }
          }
        );

        if (points.length) {
          gsap.fromTo(points,
            { opacity: 0, x: -20 },
            {
              opacity: 1, x: 0, duration: 0.55, stagger: 0.08, ease: "power2.out",
              scrollTrigger: { trigger: row, start: "top 68%", once: true }
            }
          );
        }

        if (cta) {
          gsap.fromTo(cta,
            { opacity: 0, y: 16 },
            {
              opacity: 1, y: 0, duration: 0.7, ease: "power3.out",
              scrollTrigger: { trigger: row, start: "top 65%", once: true }
            }
          );
        }
      });

      /* ─── DESTINATIONS ─── stagger + scrub parallax on each image */
      gsap.fromTo(".sp-dest-header > *",
        { opacity: 0, y: 22 },
        {
          opacity: 1, y: 0, duration: 0.85, stagger: 0.1, ease: "power3.out",
          scrollTrigger: { trigger: destRef.current, start: "top 78%", once: true }
        }
      );

      gsap.fromTo(".sp-dest-card",
        { opacity: 0, y: 40 },
        {
          opacity: 1, y: 0, duration: 0.8, stagger: 0.1, ease: "power3.out",
          scrollTrigger: { trigger: ".sp-dest-grid", start: "top 80%", once: true }
        }
      );

      gsap.utils.toArray(".sp-dest-card img").forEach((img) => {
        gsap.fromTo(img,
          { yPercent: -6 },
          {
            yPercent: 6, ease: "none",
            scrollTrigger: {
              trigger: img,
              start: "top bottom",
              end: "bottom top",
              scrub: true,
            }
          }
        );
      });

      /* ─── PROCESS ─── drawing connector line + step reveals */
      gsap.fromTo(".sp-process-header > *",
        { opacity: 0, y: 22 },
        {
          opacity: 1, y: 0, duration: 0.85, stagger: 0.1, ease: "power3.out",
          scrollTrigger: { trigger: processRef.current, start: "top 78%", once: true }
        }
      );

      if (procLineRef.current) {
        const path = procLineRef.current.querySelector("path");
        if (path) {
          const len = path.getTotalLength();
          gsap.set(path, { strokeDasharray: len, strokeDashoffset: len });
          gsap.to(path, {
            strokeDashoffset: 0,
            ease: "none",
            scrollTrigger: {
              trigger: processRef.current,
              start: "top 65%",
              end: "bottom 75%",
              scrub: 1,
            },
          });
        }
      }

      gsap.utils.toArray(".sp-step").forEach((step, i) => {
        gsap.fromTo(step,
          { opacity: 0, y: 32 },
          {
            opacity: 1, y: 0, duration: 0.7, delay: i * 0.12, ease: "power3.out",
            scrollTrigger: { trigger: processRef.current, start: "top 70%", once: true }
          }
        );
      });

      /* ─── CTA ─── fade up + big background text drifts */
      gsap.fromTo(".sp-cta-inner > *",
        { opacity: 0, y: 24 },
        {
          opacity: 1, y: 0, duration: 0.9, stagger: 0.12, ease: "power3.out",
          scrollTrigger: { trigger: ctaRef.current, start: "top 78%", once: true }
        }
      );

      if (ctaBgRef.current) {
        gsap.to(ctaBgRef.current, {
          xPercent: -20,
          ease: "none",
          scrollTrigger: {
            trigger: ctaRef.current,
            start: "top bottom",
            end: "bottom top",
            scrub: 1.2,
          },
        });
      }

    }, rootRef);

    return () => ctx.revert();
  }, []);

  return (
    <div className="services-page" ref={rootRef}>
      <Helmet>
        <title>Our Services | ISTP Overseas — IELTS, Visa, University Admissions</title>
        <meta name="description" content="ISTP Overseas offers end-to-end study abroad services — IELTS coaching, university selection, SOP writing, visa assistance and scholarship guidance." />
        <link rel="canonical" href="https://www.istpoverseas.com/services" />
        <meta property="og:title" content="Services | ISTP Overseas" />
        <meta property="og:description" content="IELTS coaching, visa assistance, university admissions and scholarship guidance from ISTP Overseas." />
        <meta property="og:url" content="https://www.istpoverseas.com/services" />
      </Helmet>
      {/* ═══════ HERO ═══════ */}
      <section className="sp-hero">
        <div className="sp-hero-bg" aria-hidden="true" />
        <div className="sp-hero-inner">
          <span className="sp-hero-eyebrow sp-eyebrow">What We Offer</span>
          <h1 className="sp-hero-title">
            <span className="line-mask"><span className="line-inner">Every service you need</span></span>
            <span className="line-mask"><span className="line-inner">to <em>study abroad</em></span></span>
            <span className="line-mask"><span className="line-inner">successfully.</span></span>
          </h1>
          <p className="sp-hero-sub">
            From deciding what to study to landing in your dream country —
            ISTP provides end-to-end support at every stage of your
            international education journey.
          </p>
          <div className="sp-hero-pills">
            <span>Career Counselling</span>
            <span>University Selection</span>
            <span>Visa Assistance</span>
            <span>Test Prep</span>
            <span>Pre-Departure</span>
          </div>
        </div>
      </section>

      {/* ═══════ SERVICES LIST ═══════ */}
      <section className="sp-services" ref={servicesRef}>
        {/* Reading progress rail */}
        <div className="sp-services-progress-rail" aria-hidden="true">
          <span className="sp-services-progress-bar" ref={servicesProgressRef} />
        </div>

        <div className="sp-services-inner">
          {services.map((s) => (
            <article className="sp-service-row" key={s.id}>
              <div className="sp-service-left">
                <span className="sp-service-bg-num" aria-hidden="true">{s.id}</span>
                <div className="sp-service-icon-wrap">
                  <div className="sp-service-icon">{s.icon}</div>
                  <span className="sp-service-id-label">Service {s.id}</span>
                </div>
              </div>

              <div className="sp-service-body">
                <h2 className="sp-service-title">
                  <span className="line-mask"><span className="line-inner">{s.title}</span></span>
                </h2>
                <p className="sp-service-headline">{s.headline}</p>
                <p className="sp-service-desc">{s.desc}</p>
                <ul className="sp-service-points">
                  {s.points.map((p, j) => (
                    <li key={j}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>
                <Link to="/book-slot" className="sp-service-cta">
                  Book a free session
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="5" y1="12" x2="19" y2="12" />
                    <polyline points="12 5 19 12 12 19" />
                  </svg>
                </Link>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* ═══════ DESTINATIONS ═══════ */}
      <section className="sp-destinations" ref={destRef}>
        <div className="sp-dest-header">
          <span className="sp-eyebrow">Where They Go</span>
          <h2 className="sp-section-title">Our students study in.</h2>
          <p className="sp-section-sub">
            Placements across 15+ countries at top-ranked universities.
          </p>
        </div>
        <div className="sp-dest-grid">
          {destinations.map((d, i) => (
            <div className="sp-dest-card" key={i} onClick={() => navigate(d.slug)} style={{ cursor: "pointer" }}>
              <div className="sp-dest-img-wrap">
                <img src={d.img} alt={d.name} />
              </div>
              <div className="sp-dest-overlay">
                <h3>{d.name}</h3>
                <span>{d.count} Students</span>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ═══════ PROCESS ═══════ */}
      <section className="sp-process" ref={processRef}>
        <div className="sp-process-header">
          <span className="sp-eyebrow sp-eyebrow--center">How We Work</span>
          <h2 className="sp-section-title">
            A structured, <em>transparent</em> process.
          </h2>
          <p className="sp-section-sub">No surprises — just clear steps to your destination.</p>
        </div>
        <div className="sp-steps">
          <svg
            className="sp-process-line-svg"
            ref={procLineRef}
            viewBox="0 0 1000 60"
            preserveAspectRatio="none"
            aria-hidden="true"
          >
            <path d="M 120 28 L 880 28" />
          </svg>
          {processSteps.map((step, i) => (
            <div className="sp-step" key={i}>
              <div className="sp-step-num">{step.num}</div>
              <h3>{step.label}</h3>
              <p>{step.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ═══════ CTA ═══════ */}
      <section className="sp-cta" ref={ctaRef}>
        <div className="sp-cta-bg" ref={ctaBgRef} aria-hidden="true">START</div>
        <div className="sp-cta-inner">
          <div>
            <h2>Start with a <em>free</em><br />profile evaluation.</h2>
            <p>No commitment. Just clarity on your path forward.</p>
          </div>
          <div className="sp-cta-btns">
            <Link to="/book-slot" className="btn-primary" style={{ padding: '14px' }}>
              Book free session
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="5" y1="12" x2="19" y2="12" />
                <polyline points="12 5 19 12 12 19" />
              </svg>
            </Link>
            <Link to="/student-meets" className="btn-outline-white">View webinars</Link>
          </div>
        </div>
      </section>

      {/* WhatsApp Float */}
      <div className="whatsapp-wrapper">
        <div className="whatsapp-tooltip">Chat with us on WhatsApp</div>
        <a
          href="https://wa.me/919110714332"
          target="_blank"
          rel="noopener noreferrer"
          className="whatsapp-float"
          aria-label="Chat on WhatsApp"
        >
          <img
            src="https://img.icons8.com/color/480/000000/whatsapp--v1.png"
            alt="WhatsApp"
            style={{ width: "42px", height: "42px", objectFit: "contain" }}
          />
        </a>
      </div>
    </div>
  );
}